import React, { useEffect, useMemo, useState } from 'react';
import { CircularProgress } from '@material-ui/core';
import { useDispatch } from 'react-redux';
import * as Actions from '@/app/store/actions';
import dayjs from 'dayjs';
import { createEvent, getEventStudent, getEventType } from '@/app/services/events/events';
import { getAllRooms } from '@/app/services/rooms/rooms';
import PageHeader from '@/app/customComponents/PageHeader';
import { useNavigate, useLocation } from 'react-router-dom';
import EventForm from './EventForm';

const CreateEvents = () => {
	const dispatch = useDispatch();
	const navigate = useNavigate();
	const location = useLocation();

	const [types, setTypes] = useState([]);
	const [rooms, setRooms] = useState([]);
	const [isLoading, setIsLoading] = useState(true);
	const [isSubmitting, setIsSubmitting] = useState(false);

	useEffect(() => {
		let isMounted = true;
		(async () => {
			try {
				const [typesRes, roomsRes] = await Promise.all([getEventType(), getAllRooms()]);
				if (!isMounted) return;

				const prioritized = [
					...typesRes.data.filter((t) => t.id === 39),
					...typesRes.data.filter((t) => t.id !== 39).sort((a, b) => String(a.type).localeCompare(String(b.type))),
				];

				const normalizedRooms = (roomsRes.data || []).map((r) => ({
					...r,
					id: Number(r.id),
				}));

				setTypes(prioritized);
				setRooms(normalizedRooms);
			} finally {
				if (isMounted) setIsLoading(false);
			}
		})();
		return () => {
			isMounted = false;
		};
	}, []);

	const initialForm = useMemo(
		() => ({
			student_id: '',
			type: '',
			date: location?.state?.date ? dayjs(location.state.date) : dayjs(),
			custom_type: '',
			room_id: [],
			description: '',
		}),
		[location?.state?.date]
	);

	const onSubmit = (payload) => {
		setIsSubmitting(true);
		createEvent(payload)
			.then(() => {
				dispatch(Actions.showMessage({ variant: 'success', message: 'Event successfully created.' }));
				navigate(-1);
			})
			.catch(() => {
				dispatch(Actions.showMessage({ variant: 'error', message: 'Failed to create event.' }));
			})
			.finally(() => setIsSubmitting(false));
	};

	const getStudentsPage = async (page, roomId) => {
		try {
			const res = await getEventStudent(page, Number(roomId));
			return res;
		} catch (e) {
			return { data: { current_page: page || 1, last_page: page || 1, data: [] } };
		}
	};

	const handleGoBack = () => navigate(-1);

	return (
		<div className="overflow-auto mb-64 pb-64">
			<div className="w-11/12 mt-32 mx-auto mb-80">
				<PageHeader onBack={handleGoBack} title="Create Event" />
				<div className=" px-32 md:px-64 py-32 bg-white rounded-8 my-32">
					{isLoading ? (
						<div className="flex justify-center">
							<CircularProgress />
						</div>
					) : (
						<EventForm
							initialForm={initialForm}
							types={types}
							rooms={rooms}
							getStudentsPage={getStudentsPage}
							submitLabel="Add"
							onSubmit={onSubmit}
							onCancel={handleGoBack}
							isSubmitting={isSubmitting}
						/>
					)}
				</div>
			</div>
		</div>
	);
};

export default CreateEvents;
